import { Box, Typography } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import {
  SPACE_BLOCK,
  belowMobile,
  desktopOnly
} from "../../components/UserInfo";

export interface PageHeadingProps {
  /**
   * ControlCenter's outlet context — the **signed-in** operator's session.
   *
   * Optional because the page renders before the context resolves; the subheading is simply absent
   * until it arrives.
   */
  ccUserInfo?: ControlcenterAPIInterfaceExternal;
}

/**
 * FR-002's page heading — "User Manager" — and FR-019a's operator subheading beneath it.
 *
 * ## ⚠️ `UM_PAGE_HEADING` is a TOOL NAME and MUST NOT be localised
 *
 * Reviewer decision, 2026-09-15. It is registered in Label Services as **do-not-translate**, with
 * `en_US`, `en_CA` and `fr_CA` all set to the literal `User Manager`. It still goes through
 * `Locale.getText` because Configuration **Principle VI** allows no UI text outside `labels.json` —
 * the string is pinned by the *registration*, not by hardcoding it here.
 *
 * **Do NOT repurpose either registered "User Manager" key**, however tempting a free translation
 * looks. Both carry one:
 *
 * | Registry key | `fr_CA` |
 * |---|---|
 * | `USER_MANAGER` | `Gestion d''utlisateurs` — and note "utlisateurs" is **misspelled**, with a doubled apostrophe |
 * | `core.dashboard.USER_MANAGER` | `Gestionnaire d'utilisateurs` |
 *
 * Adopting either renders the `h1` as "Gestionnaire d'utilisateurs" in `fr-CA`, which is exactly
 * what this decision forbids. (`core.dashboard.USER_MANAGER` is independently disqualified: keys must
 * be `SCREAMING_SNAKE_CASE`, and a dotted key collides with i18next's default `keySeparator` — see
 * `labels.spec.tsx`.)
 *
 * The registry is genuinely split on this, which is why the decision is recorded rather than
 * inferred: `CONTROLCENTER`, `inventory.INVENTORY_MANAGER` and `DDC` are all left untranslated in
 * `fr_CA`, while `FEE_MANAGER` becomes "Gestionnaire de frais". The settled part of the convention is
 * narrower and still holds — where a product name sits *inside* a sentence the prose is translated
 * and the noun is preserved, as `UM_PW_UNAVAILABLE` does with "ControlCenter". `UM_TABS_ARIA_LABEL`
 * ("User Manager areas") follows that rule and **is** translatable; this bare heading does not.
 *
 * By contrast My Info's heading **is** translated (`MANAGE_YOUR_USER_INFORMATION` → "Gérer vos
 * informations utilisateur") and that is not an inconsistency: it is an instruction, not a name.
 *
 * **No breadcrumb (FR-003).** The approved prototype still draws
 * `ControlCenter / Configuration / User Manager` above the heading and it is **deliberately not
 * reproduced** — the sibling My Info page dropped its breadcrumb for the same reason. Do not add one
 * back from the prototype; FR-003 is the ratified position.
 *
 * Sits on the app background, outside the card — the same split My Info made when the card frame
 * landed, because the `h1` is page furniture while the identity row is the card's first row.
 *
 * ## The subheading: WHO YOU ARE SIGNED IN AS (FR-019a, CC6 parity)
 *
 * Reviewer request, 2026-09-17. CC6 prints the operator's own identity beneath the tool name —
 * `Ape Zone665 (apezone665) • Bridge ID: BRIDGEapezone665` — so whoever is driving the page can see
 * which credentials they are holding, Bridge ID included.
 *
 * ### ⚠️ This is the SIGNED-IN user, NOT the as-user and NOT the selected row
 *
 * **Corrected 2026-09-18** after it shipped reading the as-user: an operator signed in as `drdoro`
 * saw the impersonated account's identity here, or none at all, and their own Bridge ID nowhere.
 * Three different people are in play on this page, and each has exactly one place:
 *
 * | Whose | Where | Source |
 * |---|---|---|
 * | **the signed-in operator** | this subheading | `ccUserInfo.internalUserId` + `first`/`last` + `bridge_user_name` |
 * | the as-user (account in context) | nothing renders it today | `ccUserInfo.userId` / `accountId` |
 * | the **selected** row | the card's `IdentityHeader` | the profile + Bridge ID reads, keyed on that row |
 *
 * ### Why this needs no query, unlike the card's
 *
 * Every value is already on the context, and the two fields that are *wrong* for describing a
 * subject are exactly right here:
 *
 * - **`first`/`last`** are copied off the session JWT, so they always name the signed-in operator —
 *   the upstream defect `IdentityHeader` has to work around, and the value this row wants.
 * - **`bridge_user_name`** is looked up against `internalUserId || userId`, and `internalUserId` is
 *   set for every internal user, so it is always the **operator's** bridge username. `IdentityHeader`
 *   must therefore avoid it and call `useGetBridgeId` per subject; here it is free and correct.
 * - **`internalUserId`** is the operator's CC username (`userId` is swapped to the as-user while
 *   impersonating). Falls back to `userId` for the non-internal case, which this internal-only page
 *   does not reach but which keeps the row honest if it is ever mounted elsewhere.
 *
 * So do **not** "make this consistent" by pointing it at `useGetBridgeId` or the profile read: that
 * would re-introduce the as-user bug this correction removed, and pay for two requests to do it.
 *
 * ### Every part is omitted when absent, separator included
 *
 * A Bridge ID is genuinely absent for a user who never registered one (11 of 42 `dealerdemo` users
 * have a profile at all, verified live 2026-09-18), and the name is absent if the JWT carries none.
 * Built by joining the parts that exist rather than by interpolating a fixed template, so there is
 * no arrangement in which a separator can outlive its operand.
 */
export const PageHeading = ({ ccUserInfo }: PageHeadingProps) => {
  const username = ccUserInfo?.internalUserId || ccUserInfo?.userId;

  const name = [ccUserInfo?.first, ccUserInfo?.last]
    .filter(part => Boolean(part?.trim()))
    .join(" ")
    .trim();

  const bridgeId = ccUserInfo?.bridge_user_name?.trim();

  /*
   * `name (username)` when the name is known, `(username)` when it is not — never
   * `username (username)`, which is what a `name || username` fallback would print.
   */
  const identityParts = [
    username ? `${name ? `${name} ` : ""}(${username})` : "",
    /*
     * ⚠️ **A LITERAL, and a deliberate exception to Configuration Principle VI** ("Localized Labels
     * — No Hardcoded UI Text", FR-011/SC-007). Reviewer decision, 2026-09-18: "since we are not
     * translating 'Bridge ID' via locales this can be text."
     *
     * The reasoning: "Bridge ID" is a **Cox product name**, so the only correct registration would
     * have been do-not-translate with all three locales set to the same literal — a `labels.json`
     * round trip, plus a human step in Label Services, to store the string this line already holds.
     * `UM_PAGE_HEADING` above took the other path for the same kind of string, so the two are now
     * deliberately inconsistent; that is the trade accepted here.
     *
     * **Do not "fix" this by reinstating a key** without also getting it registered — an
     * unregistered key renders as the raw string `BRIDGE_ID` in `en-CA`/`fr-CA`, because `Locale`
     * sets no `fallbackLng`. That failure is invisible in `en-US`, which is the only locale most
     * tests and every local run exercise. Keep it a literal or register it properly; do not leave it
     * half-done.
     */
    bridgeId ? `Bridge ID: ${bridgeId}` : ""
  ].filter(Boolean);

  return (
    <Box sx={{ marginBottom: SPACE_BLOCK }}>
      {/*
       * ## Compacted below `md` (TA4236901)
       *
       * The `h1` and the operator line together cost ~90px, which TA4236901 calls "the most
       * valuable real estate on the screen" for orientation rather than task information. So below
       * the breakpoint the heading drops to 20px and the operator line is hidden.
       *
       * **The `h1` element stays** — only its type scale shrinks. Removing it would leave the page
       * with no level-1 heading and break the document outline that the tabs' section headings hang
       * off (FR-003 / SC-010), and `UserListPanel`'s mobile "Users" heading is deliberately an `h2`
       * underneath it rather than a replacement for it.
       *
       * `variant` is a prop, not CSS, so the size comes from a `belowMobile` `fontSize` override
       * instead of a second `Typography`. That keeps one element — a `mobileOnly`/`desktopOnly` pair of
       * headings would put two `h1`s in the DOM, and jsdom (which resolves no media query) would
       * see both.
       */}
      <Typography sx={belowMobile({ fontSize: "20px" })} variant={"h1"}>
        {Locale.getText("UM_PAGE_HEADING") as string}
      </Typography>

      {/*
       * Hidden below `md`, **not removed**. It is the only place the *operator's* own Bridge ID
       * appears (the identity row names the selected user instead), so it stays in the DOM and
       * stays available to a screen reader rather than being dropped from the page on a phone.
       */}
      {identityParts.length > 0 && (
        <Typography
          color={"sem.color.on-surface.muted"}
          data-testid={"usermanager-context-identity"}
          sx={desktopOnly("block")}
          tag={"p"}
          variant={"body-sm"}
        >
          {identityParts.join(" • ")}
        </Typography>
      )}
    </Box>
  );
};
